/**
 * CategoryServicesPage — generic services CRUD hub.
 *
 * A parameterised mirror of `VisaIdServices.js` that renders the exact same
 * UX (grid + toggles + edit modal + details tab + required-documents tab)
 * for any sibling catalog. We use it for:
 *   • Traffic & Licensing        → /control/cloudgm/traffic-services
 *   • Document Authentication    → /control/cloudgm/document-auth
 *
 * All category-specific config (API base, colour, icon set, breadcrumb,
 * service-type options, back paths) is passed in via `config`.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Plus, Edit3, Trash2, Loader2, Check, MapPin,
  RefreshCcw, ArrowLeftRight, Car, IdCard, AlertTriangle, Stamp, Award,
  Landmark, FileCheck, Languages, Settings, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import EmirateDocsSection from '../components/services/EmirateDocsSection';
import ServiceFormModal from '../components/services/ServiceFormModal';
import EmiratePricingPanel from '../components/services/EmiratePricingPanel';
import { pricingSummary } from '../components/services/serviceEmirates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Full icon dictionary shared across all categories.
const SERVICE_ICONS = {
  RefreshCcw, ArrowLeftRight, Car, IdCard, AlertTriangle, Stamp, Award,
  Landmark, FileCheck, Languages, FileText,
};

export default function CategoryServicesPage({ config }) {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const { language } = useLanguage();
  const { token } = useAuth();

  const {
    apiPath,             // e.g. '/api/traffic-services'
    docsServiceType,     // e.g. 'traffic_services' — key for master-documents mapping
    listPath,            // e.g. '/control/cloudgm/traffic-services'
    typeOptions,         // [{ value, labelAr, labelEn }] — dropdown enum for service_type
    defaultServiceType,  // e.g. 'vehicle'
    accent,              // hex or tailwind colour code — used in header pills and buttons
    accentTw,            // { icon: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20', switchOn: 'data-[state=checked]:bg-orange-500', ... }
    pageIcon,            // lucide component reference (not a string)
    titleAr,             // 'خدمات المرور والترخيص'
    titleEn,             // 'Traffic & Licensing Services'
    subtitleAr,          // 'إدارة خدمات المرور وترخيص المركبات والسائقين'
    subtitleEn,
    breadcrumb,          // [{ labelAr, labelEn, path }]
    backPath,            // '/control/cloudgm?view=printing-documents'
    dataTestidPrefix,    // 'traffic'
  } = config;

  const PageIcon = pageIcon || FileText;

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const [savingEmirates, setSavingEmirates] = useState(false);

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}${apiPath}`, { headers: getHeaders() });
      if (response.data.success) setServices(response.data.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [apiPath]);

  useEffect(() => {
    if (serviceId && services.length > 0) {
      const service = services.find((s) => s.id === serviceId);
      if (service) setSelectedService(service);
    }
  }, [serviceId, services]);

  const openAddModal = () => {
    setEditingService(null);
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setShowModal(true);
  };

  // Simplified form (Jun 2026): main/sub name + descriptions + active. Pricing is per emirate.
  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API_URL}${apiPath}/${editingService.id}`, payload, { headers: getHeaders() });
        toast.success(language === 'ar' ? 'تم تحديث الخدمة بنجاح' : 'Service updated successfully');
      } else {
        await axios.post(`${API_URL}${apiPath}`, payload, { headers: getHeaders() });
        toast.success(language === 'ar' ? 'تم إضافة الخدمة بنجاح' : 'Service added successfully');
      }
      setShowModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'خطأ في حفظ البيانات' : 'Error saving data'));
    } finally {
      setSaving(false);
    }
  };

  const saveEmirates = async (emirate_pricing) => {
    setSavingEmirates(true);
    try {
      await axios.put(`${API_URL}${apiPath}/${selectedService.id}`, { emirate_pricing }, { headers: getHeaders() });
      const r = await axios.get(`${API_URL}${apiPath}/${selectedService.id}`, { headers: getHeaders() });
      if (r.data?.data) setSelectedService(r.data.data);
      toast.success(language === 'ar' ? 'تم حفظ أسعار الإمارات' : 'Emirate prices saved');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'خطأ في حفظ الأسعار' : 'Error saving prices'));
    } finally {
      setSavingEmirates(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}${apiPath}/${id}`, { headers: getHeaders() });
      toast.success(language === 'ar' ? 'تم حذف الخدمة بنجاح' : 'Service deleted successfully');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(language === 'ar' ? 'خطأ في حذف الخدمة' : 'Error deleting service');
    } finally {
      setDeleting(null);
    }
  };

  const toggleServiceActive = async (service) => {
    const next = !service.is_active;
    setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, is_active: next } : s)));
    try {
      await axios.put(`${API_URL}${apiPath}/${service.id}`, { is_active: next }, { headers: getHeaders() });
      toast.success(next
        ? (language === 'ar' ? 'تم تفعيل الخدمة' : 'Service activated')
        : (language === 'ar' ? 'تم تعطيل الخدمة' : 'Service deactivated'));
    } catch (error) {
      setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, is_active: !next } : s)));
      toast.error(language === 'ar' ? 'فشل التحديث' : 'Update failed');
    }
  };

  const viewService = (service) => {
    setSelectedService(service);
    setActiveTab('details');
    navigate(`${listPath}/${service.id}`);
  };

  // Details view
  if (selectedService) {
    const IconComponent = SERVICE_ICONS[selectedService.icon] || FileText;
    const detailBreadcrumb = [
      ...breadcrumb,
      { labelAr: selectedService.name_ar, labelEn: selectedService.name_en },
    ];

    return (
      <DashboardLayout
        title={language === 'ar' ? selectedService.name_ar : selectedService.name_en}
        titleEn={selectedService.name_en}
        subtitle={language === 'ar' ? 'إدارة بيانات الخدمة والأوراق المطلوبة' : 'Manage service details and required documents'}
        subtitleEn="Manage service details and required documents"
        backPath={listPath}
        onBack={() => {
          setSelectedService(null);
          navigate(listPath);
        }}
        breadcrumb={detailBreadcrumb}
      >
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid={`${dataTestidPrefix}-service-detail`}>
          <div className="flex items-center gap-4 bg-[#1a1a2e] border border-[#2a2a4e] rounded-2xl p-5">
            <div className={`w-14 h-14 rounded-xl ${accentTw.bg} ${accentTw.border} border flex items-center justify-center shrink-0`}>
              <IconComponent className={`h-7 w-7 ${accentTw.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate">
                {language === 'ar' ? selectedService.name_ar : selectedService.name_en}
              </h1>
              <div className="flex items-center gap-4 text-sm text-[#A1A1AA] mt-1">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{pricingSummary(selectedService, language === 'ar').count}/7 {language === 'ar' ? 'إمارات' : 'emirates'} · {pricingSummary(selectedService, language === 'ar').text}</span>
                <span className={`flex items-center gap-1 ${selectedService.is_active ? 'text-emerald-300' : 'text-red-300'}`}>
                  <Check className="h-4 w-4" />{selectedService.is_active ? (language === 'ar' ? 'مفعّل' : 'Active') : (language === 'ar' ? 'معطّل' : 'Inactive')}
                </span>
              </div>
            </div>
            <Button
              onClick={() => openEditModal(selectedService)}
              variant="outline"
              className="border-[#2a2a4e] text-white shrink-0"
              data-testid={`${dataTestidPrefix}-edit-service-btn`}
            >
              <Edit3 className="h-4 w-4 me-2" />
              {language === 'ar' ? 'تعديل' : 'Edit'}
            </Button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg transition-colors ${activeTab === 'details' ? 'bg-[#c9a227] text-black' : 'bg-[#1a1a2e] text-white hover:bg-[#2a2a4e]'}`}
              data-testid={`${dataTestidPrefix}-tab-details`}
            >
              <Eye className="h-4 w-4 inline me-2" />
              {language === 'ar' ? 'تفاصيل الخدمة' : 'Service Details'}
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 rounded-lg transition-colors ${activeTab === 'documents' ? 'bg-[#c9a227] text-black' : 'bg-[#1a1a2e] text-white hover:bg-[#2a2a4e]'}`}
              data-testid={`${dataTestidPrefix}-tab-documents`}
            >
              <FileCheck className="h-4 w-4 inline me-2" />
              {language === 'ar' ? 'الأوراق المطلوبة' : 'Required Documents'}
            </button>
          </div>

          {activeTab === 'details' && (
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'}</p>
                  <p className="text-white font-medium">{selectedService.name_ar}</p>
                  {selectedService.main_name_ar && <p className="text-[11px] text-white/40 mt-1">{language === 'ar' ? 'الخدمة الرئيسية:' : 'Main service:'} {selectedService.main_name_ar}</p>}
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'}</p>
                  <p className="text-white font-medium">{selectedService.name_en}</p>
                </div>
              </div>
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الوصف بالعربية' : 'Arabic Description'}</p>
                <p className="text-white">{selectedService.description_ar || '-'}</p>
              </div>
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الوصف بالإنجليزية' : 'English Description'}</p>
                <p className="text-white">{selectedService.description_en || '-'}</p>
              </div>
              <EmiratePricingPanel
                service={selectedService}
                onSave={saveEmirates}
                saving={savingEmirates}
                language={language}
                accentTw={accentTw}
                testIdPrefix={dataTestidPrefix}
              />
            </div>
          )}

          {activeTab === 'documents' && (
            <EmirateDocsSection
              service={selectedService}
              language={language}
              testIdPrefix={dataTestidPrefix}
            />
          )}
        </section>
      </DashboardLayout>
    );
  }

  // List view
  return (
    <DashboardLayout
      title={titleAr}
      titleEn={titleEn}
      subtitle={subtitleAr}
      subtitleEn={subtitleEn}
      backPath={backPath}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${accentTw.bg} ${accentTw.border} border flex items-center justify-center`}>
              <PageIcon className={`h-6 w-6 ${accentTw.icon}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? 'الخدمات المتاحة' : 'Available Services'}
              </h2>
              <p className="text-sm text-[#71717A]">
                {services.length} {language === 'ar' ? 'خدمة' : 'services'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={openAddModal}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
              data-testid={`${dataTestidPrefix}-add-service-btn`}
            >
              <Plus className="h-4 w-4 me-2" />
              {language === 'ar' ? 'إضافة خدمة جديدة' : 'Add New Service'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className={`h-8 w-8 animate-spin ${accentTw.icon}`} />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <PageIcon className={`h-16 w-16 mx-auto mb-4 ${accentTw.icon} opacity-30`} />
            <p className="text-lg text-[#808080]">
              {language === 'ar' ? 'لا توجد خدمات بعد' : 'No services yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = SERVICE_ICONS[service.icon] || FileText;
              return (
                <div
                  key={service.id}
                  className={`bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden ${accentTw.hoverBorder} transition-all group flex flex-col ${!service.is_active ? 'opacity-70' : ''}`}
                  data-testid={`${dataTestidPrefix}-service-card-${service.id}`}
                >
                  <div className="flex items-center justify-between px-6 pt-4 pb-2 border-b border-white/5">
                    <div className="flex items-center gap-2" data-testid={`${dataTestidPrefix}-service-active-${service.id}`}>
                      <Switch
                        checked={!!service.is_active}
                        onCheckedChange={() => toggleServiceActive(service)}
                        className={accentTw.switchOn}
                      />
                      <span className={`text-[11px] font-medium ${service.is_active ? accentTw.icon : 'text-[#71717A]'}`}>
                        {service.is_active
                          ? (language === 'ar' ? 'مفعّلة' : 'Active')
                          : (language === 'ar' ? 'موقوفة' : 'Paused')}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditModal(service)}
                        className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-[#2a2a4e] transition-colors"
                        data-testid={`${dataTestidPrefix}-service-edit-${service.id}`}
                      >
                        <Edit3 className="h-4 w-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        disabled={deleting === service.id}
                        className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-red-500/20 transition-colors"
                        data-testid={`${dataTestidPrefix}-service-delete-${service.id}`}
                      >
                        {deleting === service.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-red-400" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-red-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <div className={`w-14 h-14 rounded-xl ${accentTw.bg} flex items-center justify-center group-hover:scale-110 transition-transform mb-4`}>
                      <IconComponent className={`h-7 w-7 ${accentTw.icon}`} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {language === 'ar' ? service.name_ar : service.name_en}
                    </h3>
                    <p className="text-[#808080] text-sm mb-4 line-clamp-2">
                      {language === 'ar' ? service.description_ar : service.description_en}
                    </p>
                    <div className="flex items-center justify-between text-sm mt-auto">
                      <span className="text-[#c9a227] font-bold" data-testid={`${dataTestidPrefix}-card-total-${service.id}`}>
                        {pricingSummary(service, language === 'ar').text}
                      </span>
                      <span className="text-[#808080] flex items-center gap-1" data-testid={`${dataTestidPrefix}-card-emirates-${service.id}`}>
                        <MapPin className="h-4 w-4" />
                        {pricingSummary(service, language === 'ar').count}/7 {language === 'ar' ? 'إمارات' : 'emirates'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => viewService(service)}
                    data-testid={`${dataTestidPrefix}-service-manage-${service.id}`}
                    className={`w-full p-3 ${accentTw.bg} ${accentTw.icon} text-sm font-medium ${accentTw.hoverBg} transition-colors flex items-center justify-center gap-2 border-t border-white/5`}
                  >
                    <Settings className="h-4 w-4" />
                    {language === 'ar' ? 'إدارة الخدمة' : 'Manage Service'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <ServiceFormModal
          open={showModal}
          onOpenChange={setShowModal}
          service={editingService}
          onSubmit={handleSave}
          saving={saving}
          language={language}
          PageIcon={PageIcon}
          accentIcon={accentTw.icon}
          switchOn={accentTw.switchOn}
          testIdPrefix={dataTestidPrefix}
        />
      </section>
    </DashboardLayout>
  );
}
